/**
 * Pure helpers for the agents workspace (FOR-326): labels, the delivery
 * steps a run walks through, setup progress, error messages for the review
 * decisions, and the Cody rules for the task pickers. No React and no API
 * client, so tests/cody-agents.test.mjs can pin them.
 */

import type {
  Delivery,
  DeliveryPr,
  DeliveryTests,
  RunApproval,
  RunEvent,
  RunOutcomeFilter,
  RunPhase,
  RunSummary,
  RunUsage,
  SetupStatus,
  SetupStep,
  SweepItem as SweepItemWire,
  Verdict,
  WaitingReason,
} from "@/lib/agents-api"

/* ── Phases and outcomes ───────────────────────────────────────────────── */

export const PHASES: RunPhase[] = [
  "queued", "working", "reviewing", "waiting", "revising", "merging",
  "watching", "shipped", "reverted", "rejected", "diagnosed", "failed",
]

export const PHASE_LABEL: Record<RunPhase, string> = {
  queued: "Queued",
  working: "Working",
  reviewing: "Reviewer checking",
  waiting: "Waiting for you",
  revising: "Revising",
  merging: "Merging",
  watching: "Error watch",
  shipped: "Shipped",
  reverted: "Reverted",
  rejected: "Rejected",
  diagnosed: "Diagnosed",
  failed: "Failed",
}

export function phaseLabel(phase: string | null | undefined): string {
  return (phase && PHASE_LABEL[phase as RunPhase]) || (phase ? phase[0].toUpperCase() + phase.slice(1) : "—")
}

export type Tone = "quiet" | "amber" | "blue" | "green" | "red" | "outline"

// Same rule as RunStatusChip: amber is burning time, blue waits on a person,
// green is settled, red is trouble.
const PHASE_TONE: Record<RunPhase, Tone> = {
  queued: "quiet",
  working: "amber",
  reviewing: "amber",
  revising: "amber",
  merging: "amber",
  watching: "amber",
  waiting: "blue",
  shipped: "green",
  reverted: "red",
  failed: "red",
  rejected: "outline",
  diagnosed: "quiet",
}

export function phaseTone(phase: string | null | undefined): Tone {
  return PHASE_TONE[phase as RunPhase] ?? "quiet"
}

/** Phases where something is happening right now (pulse, poll). */
export const LIVE_PHASES: RunPhase[] = ["working", "reviewing", "revising", "merging", "watching"]

/** The API's `outcome=open`: anything not finished. */
export const OPEN_PHASES: RunPhase[] = ["queued", "working", "reviewing", "waiting", "revising", "merging", "watching"]

export function isLivePhase(phase: string | null | undefined): boolean {
  return LIVE_PHASES.includes(phase as RunPhase)
}

export function isOpenPhase(phase: string | null | undefined): boolean {
  return OPEN_PHASES.includes(phase as RunPhase)
}

export const OUTCOME_FILTERS: { value: RunOutcomeFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "merged", label: "Merged" },
  { value: "shipped", label: "Shipped" },
  { value: "reverted", label: "Reverted" },
  { value: "rejected", label: "Rejected" },
  { value: "diagnosed", label: "Diagnosed" },
  { value: "failed", label: "Failed" },
]

/** The most specific outcome bucket a phase falls in (mirrors the API's OUTCOMES). */
export function phaseOutcome(phase: string | null | undefined): RunOutcomeFilter {
  if (isOpenPhase(phase)) return "open"
  if (phase === "shipped" || phase === "reverted" || phase === "rejected" || phase === "diagnosed" || phase === "failed") return phase
  return "open"
}

export const WAITING_LABEL: Record<string, string> = {
  red: "Red zone",
  calibration: "Calibration",
  level: "Autonomy level",
  ci: "CI running",
  github: "PRs unverified",
  watch: "Sentry not connected",
}

export function waitingLabel(reason: WaitingReason): string {
  return WAITING_LABEL[reason] ?? reason
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  approve: "Approve",
  concerns: "Approve with concerns",
  changes: "Request changes",
}

export function runPath(id: number): string {
  return `/agents/runs/${id}`
}

/* ── Autonomy levels and products ──────────────────────────────────────── */

export const LEVELS: { level: number; name: string; does: string }[] = [
  { level: 0, name: "Investigate", does: "Diagnosis and plan only, no code" },
  { level: 1, name: "Draft PR", does: "Opens a PR; you approve the merge" },
  { level: 2, name: "Ship to staging", does: "Merges to staging once CI is green" },
  { level: 3, name: "Auto-ship small fixes", does: "Merges green, small, approved changes itself" },
]

export function levelName(level: number | null | undefined): string {
  return LEVELS.find((l) => l.level === level)?.name ?? "—"
}

const PRODUCTS: Record<string, { label: string; board: string; color: string }> = {
  forge: { label: "Forge", board: "FOR", color: "#e5484d" },
  stm: { label: "SpeechTherapyMagic", board: "STM", color: "#8e4ec6" },
  ferrylend: { label: "Ferrylend", board: "FER", color: "#0090ff" },
}

/** A product's label, board key and tile colour; unknown products get a grey tile. */
export function productMeta(product: string | null | undefined, boardKey?: string | null): { label: string; board: string; color: string } {
  const known = product ? PRODUCTS[product] : undefined
  if (known) return known
  const label = product ? product[0].toUpperCase() + product.slice(1) : boardKey ?? "—"
  return { label, board: boardKey ?? label.slice(0, 3).toUpperCase(), color: "#6B7280" }
}

export const KNOWN_PRODUCTS = Object.keys(PRODUCTS)

/** "justingluska/Gluska-Forge-API" → "Gluska-Forge-API". */
export function repoName(repository: string | null | undefined): string {
  if (!repository) return "—"
  return repository.split("/").pop() || repository
}

/* ── Avatars ───────────────────────────────────────────────────────────── */

const WORKSPACE_FACES = ["cody", "reviewer", "sweeper"]

/**
 * The agent's face: an image path the API stored, or the Notionists SVG for
 * Cody, the Reviewer and the Sweeper. Null for an emoji avatar (desk agents).
 */
export function agentImage(agent: { slug?: string | null; avatar?: string | null } | null | undefined): string | null {
  if (!agent) return null
  const avatar = agent.avatar ?? ""
  if (avatar.startsWith("/") || avatar.startsWith("https://")) return avatar
  if (agent.slug && WORKSPACE_FACES.includes(agent.slug)) return `/avatars/agents/${agent.slug}.svg`
  return null
}

/* ── Delivery ──────────────────────────────────────────────────────────── */

export function deliveryPrs(delivery: Delivery | null | undefined): DeliveryPr[] {
  return Array.isArray(delivery?.prs) ? delivery!.prs! : []
}

/** Every changed file across the run's PRs, tagged with its repository. */
export function deliveryFiles(delivery: Delivery | null | undefined): { repository: string; path: string; add: number; del: number }[] {
  return deliveryPrs(delivery).flatMap((pr) =>
    (pr.files ?? []).map((f) => ({ repository: pr.repository, path: f.path, add: Number(f.add) || 0, del: Number(f.del) || 0 })),
  )
}

/** Lines added and removed across the PRs: the PR totals, else the files summed. */
export function deliveryLines(delivery: Delivery | null | undefined): { add: number; del: number } {
  return deliveryPrs(delivery).reduce(
    (sum, pr) => {
      const files = pr.files ?? []
      const add = pr.additions ?? files.reduce((n, f) => n + (Number(f.add) || 0), 0)
      const del = pr.deletions ?? files.reduce((n, f) => n + (Number(f.del) || 0), 0)
      return { add: sum.add + add, del: sum.del + del }
    },
    { add: 0, del: 0 },
  )
}

export type StepState = "done" | "active" | "failed" | "pending" | "skipped"

export interface DeliveryStep {
  key: "pr" | "reviewer" | "approve" | "merge" | "watch"
  label: string
  detail?: string
  state: StepState
  at?: string | null
}

/**
 * PR → Reviewer → Justin approves → Merge → Error watch, from the run's
 * phase and delivery record. The run page and the task panel both draw it.
 */
export function deliverySteps(run: Pick<RunSummary, "phase" | "delivery" | "waiting_because">, approvals: RunApproval[] = []): DeliveryStep[] {
  const d = run.delivery ?? {}
  const phase = run.phase
  const prs = deliveryPrs(d)
  const reviewer = d.reviewer ?? null
  const merge = d.merge ?? null
  const watch = d.watch ?? null
  const approved = approvals.find((a) => a.decision === "approved")
  const merged = merge?.state === "merged"
  const noCode = prs.length === 0 && (phase === "diagnosed" || phase === "shipped")
  const after = (state: StepState): StepState => (phase === "failed" || phase === "rejected" || noCode ? "skipped" : state)

  const ci = d.ci ?? null
  const ciDetail = ci?.state === "running" ? "CI running" : ci?.state === "failed" ? "CI failing" : ci?.state === "none" ? "No CI" : null
  const pr: DeliveryStep = {
    key: "pr",
    label: "PR opened",
    state:
      prs.length > 0 ? (ci?.state === "failed" ? "failed" : ci?.state === "running" ? "active" : "done")
      : noCode ? "skipped"
      : phase === "failed" ? "failed"
      : phase === "working" || phase === "revising" ? "active"
      : "pending",
    detail: [prs.length > 1 ? `${prs.length} PRs` : null, noCode ? "No code" : null, prs.length > 0 ? ciDetail : null, run.waiting_because?.includes("github") ? "Unverified" : null].filter(Boolean).join(" · ") || undefined,
  }

  const review: DeliveryStep = {
    key: "reviewer",
    label: "Reviewer",
    state: reviewer ? "done" : phase === "reviewing" ? "active" : after("pending"),
    detail: reviewer ? VERDICT_LABEL[reviewer.verdict] ?? reviewer.verdict : undefined,
  }
  if (reviewer && d.revising) review.detail = `${review.detail} · round ${d.rounds ?? 1}`

  const partial = merge?.state === "partial"
  const autoMerged = merged && !approved
  const you: DeliveryStep = {
    key: "approve",
    label: "Justin approves",
    state:
      phase === "rejected" ? "failed"
      : approved || merged || partial ? "done"
      : phase === "waiting" ? "active"
      : after("pending"),
    detail:
      phase === "rejected" ? "Rejected"
      : autoMerged ? "Auto"
      : phase === "waiting" && run.waiting_because?.length ? run.waiting_because.map(waitingLabel).join(", ")
      : undefined,
    at: approved?.decided_at ?? null,
  }

  const mergeStep: DeliveryStep = {
    key: "merge",
    label: "Merge",
    state: merged ? "done" : merge?.state === "failed" ? "failed" : partial || phase === "merging" || merge?.state === "pending" ? "active" : after("pending"),
    detail:
      merge?.state === "failed" ? merge.error ?? "Failed"
      : partial ? `${merge?.merged?.length ?? 0} of ${prs.length} merged`
      : merge?.sha ? merge.sha.slice(0, 7)
      : undefined,
    at: merge?.at ?? null,
  }

  const watchStep: DeliveryStep = {
    key: "watch",
    label: "Error watch",
    state:
      watch?.state === "passed" || (phase === "shipped" && prs.length > 0) ? "done"
      : watch?.state === "reverted" || phase === "reverted" ? "failed"
      : watch?.state === "failed" ? "failed"
      : watch?.state === "needs_attention" || watch?.state === "watching" || watch?.state === "reverting" || phase === "watching" ? "active"
      : after("pending"),
    detail:
      watch?.state === "reverted" || phase === "reverted" ? "Reverted"
      : watch?.state === "reverting" ? "Reverting"
      : watch?.state === "needs_attention" ? "Needs you"
      : watch?.result ?? undefined,
    at: watch?.state === "watching" ? watch.until : null,
  }

  return [pr, review, you, mergeStep, watchStep]
}

/* ── Usage (counts only, never money) ──────────────────────────────────── */

/** 182400 → "182k", 1_250_000 → "1.3M". */
export function formatCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—"
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
  if (n >= 10_000) return `${Math.round(n / 1000)}k`
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`
  return String(n)
}

/** Tokens, turns and minutes for a run; minutes fall back to start → finish (or now). */
export function usageFigures(
  usage: RunUsage | null | undefined,
  times?: { started_at: string | null; finished_at: string | null },
  now = Date.now(),
): { tokens: number | null; turns: number | null; minutes: number | null; model: string | null } {
  const u = usage ?? {}
  const input = typeof u.input_tokens === "number" ? u.input_tokens : null
  const output = typeof u.output_tokens === "number" ? u.output_tokens : null
  const tokens = input == null && output == null ? null : (input ?? 0) + (output ?? 0)
  let minutes = typeof u.minutes === "number" ? u.minutes : null
  if (minutes == null && times?.started_at) {
    const start = Date.parse(times.started_at)
    const end = times.finished_at ? Date.parse(times.finished_at) : now
    if (Number.isFinite(start) && Number.isFinite(end)) minutes = Math.max(0, Math.round((end - start) / 60_000))
  }
  return {
    tokens,
    turns: typeof u.turns === "number" ? u.turns : null,
    minutes,
    model: typeof u.model === "string" ? u.model : null,
  }
}

/** A test suite's duration: seconds as "2m 44s", a string as given. */
export function formatDuration(value: number | string | null | undefined): string | null {
  if (value == null || value === "") return null
  if (typeof value === "string") return value
  const s = Math.round(value)
  if (s < 60) return `${s}s`
  return `${Math.floor(s / 60)}m ${s % 60}s`
}

/**
 * The runner's test result in one line. A failed command with no counts (a
 * type check or build, a crash, a timeout) is failing, never "All passing".
 */
export function testsVerdict(tests: DeliveryTests | null | undefined): { failing: boolean; label: string; counts: string | null } {
  if (!tests) return { failing: false, label: "No tests reported", counts: null }
  const failed = tests.failed ?? 0
  const failing = tests.ok === false || failed > 0
  const n = (v: number) => v.toLocaleString("en-US")
  const label = failed > 0 ? `${n(failed)} failing`
    : tests.timed_out ? "Timed out"
      : tests.ok === false ? (tests.exit_code != null ? `Failed (exit ${tests.exit_code})` : "Failed")
        : "All passing"
  const parts = [
    tests.passed != null ? `${n(tests.passed)} passed` : null,
    tests.skipped ? `${n(tests.skipped)} skipped` : null,
    formatDuration(tests.duration),
  ].filter(Boolean)
  return { failing, label, counts: parts.length ? parts.join(" · ") : null }
}

/* ── Setup ─────────────────────────────────────────────────────────────── */

export function setupProgress(steps: Pick<SetupStep, "status" | "required">[], blocking: string[] = []) {
  const required = steps.filter((s) => s.required)
  return {
    done: steps.filter((s) => s.status === "done").length,
    total: steps.length,
    requiredDone: required.filter((s) => s.status === "done").length,
    requiredTotal: required.length,
    complete: blocking.length === 0 && required.every((s) => s.status === "done"),
  }
}

export const SETUP_STATUS_LABEL: Record<SetupStatus, string> = { done: "Done", todo: "To do", warn: "Check" }

/** Setup goes first in the nav until it is complete and Cody has been started once. */
export function setupFirst(blocking: string[] | null | undefined, startedAt: string | null | undefined): boolean {
  return (blocking?.length ?? 0) > 0 || !startedAt
}

/** A blocking step key as a title, from the checklist when it has it. */
export function stepTitle(key: string, steps: Pick<SetupStep, "key" | "title">[] = []): string {
  return steps.find((s) => s.key === key)?.title ?? key.replace(/_/g, " ")
}

/* ── Errors ────────────────────────────────────────────────────────────── */

export type ErrorBody = { error?: string; code?: string; message?: string; errors?: Record<string, string[]>; blocking?: string[] } | null | undefined

function firstValidation(body: ErrorBody): string | null {
  const errs = body?.errors
  if (!errs) return null
  const first = Object.values(errs).flat()[0]
  return first ? String(first) : null
}

/**
 * Approve / Request changes / Reject failures, as a toast title and line.
 * `reReview` marks the one a Re-review fixes (approve before a valid verdict).
 */
export function decisionError(status: number | undefined, body: ErrorBody): { title: string; description: string; reReview?: boolean } {
  if (body?.code === "needs_review_verdict") {
    return { title: "Needs a fresh review", description: "The Reviewer has not read the current head. Re-review, then approve.", reReview: true }
  }
  if (body?.code === "merge_in_progress") {
    return { title: "Already merging", description: "Another approve is merging this run." }
  }
  if (body?.code === "merge_partial") {
    return { title: "Partly merged", description: `${(body.error ?? "Some PRs merged").replace(/\.?\s*$/, ".")} Approve again to retry the rest.` }
  }
  if (status === 409 || body?.code === "run_not_awaiting_review") {
    return { title: "Already decided", description: "This run is no longer waiting for a decision." }
  }
  if (body?.code === "merge_refused") {
    return { title: "Merge refused", description: body.error ?? "This repository does not merge on approve." }
  }
  if (status === 502 || body?.code === "merge_failed") {
    return { title: "GitHub refused the merge", description: `${(body?.error ?? "Merge failed").replace(/\.?\s*$/, ".")} Approve again to retry.` }
  }
  if (status === 422) {
    return { title: "Not saved", description: firstValidation(body) ?? body?.error ?? body?.message ?? "Check the note." }
  }
  if (status === 403) return { title: "Owner only", description: "Only an owner can decide runs." }
  return { title: "Something went wrong", description: body?.error ?? body?.message ?? "Try again." }
}

const RUN_ACTION_ERRORS: Record<string, string> = {
  nothing_to_review: "Nothing to review.",
  review_in_progress: "A review is already running.",
  head_changed: "The PR moved since delivery. Request changes instead.",
  reviewer_unavailable: "The Reviewer is paused or unavailable.",
  github_unavailable: "GitHub did not answer. Try again.",
  no_session: "No session yet: the runner has not reported one.",
  run_not_active: "This run has finished.",
  not_taken_over: "This run is not taken over.",
}

/** Re-review, Take over and Resume failures (409/502 codes). */
export function runActionError(status: number | undefined, body: ErrorBody): string {
  return (body?.code && RUN_ACTION_ERRORS[body.code]) || body?.error || body?.message || (status === 502 ? "GitHub did not answer." : "Try again.")
}

/** Start refused while setup is incomplete (409 setup_incomplete), else a generic line. */
export function startError(status: number | undefined, body: ErrorBody, steps: Pick<SetupStep, "key" | "title">[] = []): { title: string; description: string; blocking: string[] } {
  if (status === 409 && body?.code === "setup_incomplete") {
    const blocking = body.blocking ?? []
    return { title: "Finish setup first", description: blocking.map((k) => stepTitle(k, steps)).join(", "), blocking }
  }
  return { title: "Could not start", description: body?.error ?? body?.message ?? "Try again.", blocking: [] }
}

/* ── Tasks ─────────────────────────────────────────────────────────────── */

export const CODY_EMAIL = "cody@gluska.co"

export function isCodyUser(user: { email?: string | null } | null | undefined): boolean {
  return (user?.email ?? "").trim().toLowerCase() === CODY_EMAIL
}

/**
 * The people a picker offers. Only an owner may assign Cody (the API refuses
 * anyone else with a 422), only on the boards Cody belongs to (when both the
 * board and Cody's boards are known), and nobody while Cody is disabled.
 */
export function assignableUsers<T extends { email?: string | null }>(
  users: T[],
  viewerIsOwner: boolean,
  board?: { key: string | null | undefined; codyBoards: string[] | null | undefined; codyDisabled?: boolean },
): T[] {
  const codyHere = viewerIsOwner && !board?.codyDisabled && (!board?.codyBoards || !board.key || board.codyBoards.includes(board.key))
  return codyHere ? users : users.filter((u) => !isCodyUser(u))
}

/** The greyed-out picker line for a disabled Cody (the API refuses it with a 422). */
export function codyDisabledNote(reason: string | null | undefined): string {
  const why = reason?.trim()
  return why ? `Disabled: ${why}` : "Disabled"
}

/**
 * The runs a task's agent panel lists: the coder's, newest first. A reviewer
 * run is a step of the run it reviews (the Reviewer step links to it), not a
 * run of its own on the task: listed, the newest one took the panel's top
 * card and drew the coder's steps for it (run 24 on FOR-328).
 */
export function taskPanelRuns<T extends { id: number; agent?: { kind?: string | null } | null }>(runs: T[]): T[] {
  return runs.filter((r) => r.agent?.kind !== "reviewer").sort((a, b) => b.id - a.id)
}

/** One run per task: the newest open run (highest id) wins. */
export function openRunsByTask(runs: RunSummary[]): Map<number, RunSummary> {
  const map = new Map<number, RunSummary>()
  for (const run of runs) {
    const id = run.task?.id
    if (id == null || !isOpenPhase(run.phase)) continue
    const seen = map.get(id)
    if (!seen || run.id > seen.id) map.set(id, run)
  }
  return map
}

/** The bot badge on a task row while a run is open. */
export function taskRunBadge(run: Pick<RunSummary, "id" | "phase" | "agent">): { label: string; working: boolean; title: string; avatar?: string } {
  const name = run.agent?.name ?? "Cody"
  const avatar = agentImage(run.agent) ?? undefined
  const working = isLivePhase(run.phase)
  const label = run.phase === "waiting" ? "Needs you" : run.phase === "queued" ? "Queued" : name
  return { label, working, title: `${name}: ${phaseLabel(run.phase)} (run #${run.id})`, avatar }
}

/* ── Transcript and sweep records ──────────────────────────────────────── */

/**
 * The line an event reduces to. Event types are the runner's vocabulary and
 * will grow, so this reads the keys a payload plausibly carries and falls
 * back to the raw JSON rather than an empty row.
 */
export function eventText(event: Pick<RunEvent, "type" | "payload">): { headline: string; body: string | null } {
  const payload = event.payload
  if (!payload) return { headline: event.type, body: null }
  const pick = (...keys: string[]): string | null => {
    for (const key of keys) {
      const value = payload[key]
      if (typeof value === "string" && value.trim()) return value
    }
    return null
  }
  const headline = pick("headline", "name", "tool", "title", "action", "step") ?? event.type
  const body = pick("text", "message", "content", "summary", "input", "output", "result", "error")
  if (body) return { headline, body }
  const rest = Object.keys(payload).length > 0 ? JSON.stringify(payload, null, 2) : null
  return { headline, body: rest }
}

/** A sweep list entry (pinned shape), with the task key lifted out; tolerant of a missing key. */
export function sweepItem(raw: Partial<SweepItemWire> | null | undefined): SweepRow {
  const o = raw ?? {}
  return {
    title: o.title?.trim() || "Untitled",
    culprit: o.culprit ?? null,
    reason: o.reason ?? null,
    events: typeof o.events === "number" ? o.events : null,
    users: typeof o.users === "number" ? o.users : null,
    url: o.url ?? null,
    taskKey: o.task?.key ?? null,
    taskId: o.task?.id ?? null,
    runId: o.run_id ?? null,
  }
}

export interface SweepRow {
  title: string
  culprit: string | null
  reason: string | null
  events: number | null
  users: number | null
  url: string | null
  taskKey: string | null
  taskId: number | null
  runId: number | null
}
